// One clip driving several separately-skinned part GLBs as a single body.
//
// Each part carries its OWN copy of the skeleton, and that is the point: fusing them
// into a union-skeleton rig gives the clip duplicate bone names to bind against, and it
// picks one arbitrarily. Every part binds the same clip by bone name instead, so they
// move together while staying separately addressable.
//
// A part carries only the bones it needs — the face mesh has the spine/neck/head
// chain plus ~840 facial bones, not the fingers — so the clip is filtered per part.
// Binding it unfiltered logs one warning per unresolvable track (310 for the face)
// and drowns the signal these scenes exist to show.
//
// The clip itself is reduced to bone ROTATIONS first (`poseTracksOnly`), which is not an
// optimisation: the MetaHuman clip's `root` quaternion is a 90 degree turn about X that
// lays the whole body on its side.

import * as THREE from 'three'
import { playerPoolFor } from '../../lib/animPlayerPool.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { BRANCH_PARTS } from './partBranchVertices.js'
import { captureHeadRest, headRigidDelta } from '../branch/headRigidDrive.js'
import { buildRestPose } from '../../lib/additiveBake.js'
import { pruneBodyClipTracks } from '../../lib/pruneBodyClipTracks.js'

// Face, body and the three clothing pieces. Derived from the branch map rather than
// listed again: the two would drift, and a part loaded but not mapped to a branch is
// dead weight while a part mapped but not loaded throws.
export const MYRA_PART_NAMES = Object.values(BRANCH_PARTS).flat()

/** Node a track drives: `"Armature/pelvis.quaternion"` → `"pelvis"`. */
export function trackTargetName(trackName) {
  const dot = trackName.lastIndexOf('.')
  if (dot < 0) return null
  return trackName.slice(0, dot).split('/').pop()
}

/** The clip as this rig can play it: the SAME `pruneBodyClipTracks` the GLB rig applies,
 *  so a walk keeps the pelvis translation carrying its bob and weight shift. Copies, since
 *  that prune edits in place and the caller's clip may be bound elsewhere. */
export function poseTracksOnly(clip, generatedMarker = null) {
  const copy = new THREE.AnimationClip(clip.name, clip.duration, clip.tracks.slice())
  pruneBodyClipTracks(copy, ALL_NODES, { generatedMarker })
  return copy
}

// poseTracksOnly prunes by ROLE only; which bones exist is per part, and clipForPart
// answers that against the real scene.
const ALL_NODES = { has: () => true }

/** Track names split by whether `nodeNames` can resolve their target node. */
export function splitTracksByBinding(trackNames, nodeNames) {
  const bound = []
  const unbound = []
  for (const trackName of trackNames) {
    const target = trackTargetName(trackName)
    if (target && nodeNames.has(target)) bound.push(trackName)
    else unbound.push(trackName)
  }
  return { bound, unbound }
}

function loadGltf(url) {
  return new Promise((resolve, reject) => new GLTFLoader().load(url, resolve, undefined, reject))
}

/** Names of every node in `root`'s subtree. */
function subtreeNodeNames(root) {
  const names = new Set()
  root.traverse((object) => names.add(object.name))
  return names
}

/** The one skinned mesh a part GLB exists to carry. */
function partMesh(root) {
  let found = null
  root.traverse((object) => { if (!found && object.isSkinnedMesh) found = object })
  if (!found) throw new Error(`partRigDrive: part '${root.name}' has no skinned mesh`)
  return found
}

/** The skinned mesh named `name` anywhere under `root`.
 *
 *  GLTFLoader suffixes a node whose name a sibling already took, so a mesh authored as
 *  `…FaceMesh` can load as `…FaceMesh_1`; match either. */
function namedPartMesh(root, name) {
  let found = null
  root.traverse((object) => {
    if (found || !object.isSkinnedMesh) return
    if (object.name === name || object.name.replace(/_\d+$/, '') === name) found = object
  })
  if (!found) throw new Error(`partRigDrive: no skinned mesh '${name}' in the rig GLB`)
  return found
}

function countGeometry(root) {
  let bones = 0
  let meshes = 0
  let vertices = 0
  root.traverse((object) => {
    if (object.isBone) bones++
    if (object.isSkinnedMesh) { meshes++; vertices += object.geometry.attributes.position.count }
  })
  return { bones, meshes, vertices }
}

/** The subset of `clip` that resolves against `part`, as a clip of its own.
 *
 *  `dropped` names the bones this part hasn't got. A track that resolves nowhere leaves
 *  its bone at BIND pose while the rest of the body animates — a walk with rigid arms —
 *  and silently dropping it is what makes that unattributable. */
export function clipForPart(clip, part, name = part.name, dropped = null) {
  const names = subtreeNodeNames(part)
  const { bound, unbound } = splitTracksByBinding(clip.tracks.map((track) => track.name), names)
  if (dropped) for (const trackName of unbound) dropped.add(trackTargetName(trackName))
  const keep = new Set(bound)
  const tracks = clip.tracks.filter((track) => keep.has(track.name))
  return new THREE.AnimationClip(`${clip.name}@${name}`, clip.duration, tracks)
}

/** Name the bones a clip drives that this rig hasn't got — once per clip.
 *
 *  Those bones stay at BIND pose while everything else animates, which reads as a broken
 *  clip rather than as a rig missing joints. Usually it IS the rig: rig.glb carries the
 *  joints the gaussians are skinned to, not necessarily every finger the clip animates. */
const _reportedDrops = new Set()
function reportDroppedBones(clipName, dropped) {
  if (!dropped.size || _reportedDrops.has(clipName)) return
  _reportedDrops.add(clipName)
  const bones = [...dropped].filter(Boolean)
  const shown = bones.slice(0, 8).join(', ')
  console.warn(`[partRigDrive] '${clipName}' drives ${bones.length} bone(s) this rig has not got `
    + `— they hold their bind pose: ${shown}${bones.length > 8 ? ', …' : ''}`)
}

/** The head rest, or null — a part rig with no head bone still drives every other part. */
function captureHeadRestOrNull(scene) {
  if (!scene) return null
  try { return captureHeadRest(scene, 'partRigDrive: ') } catch { return null }
}

/** Clips bound to every part of a rig, played as a single body.
 *
 *  Several clips, ONE mixer per scene, so `setWeights` blends them the way the body
 *  blueprint's four state machines expect — the same `{clipName → weight}` contract
 *  GraphEvaluator.applyWeights drives the GLB mixer with. Only `clips[0]` plays at
 *  construction: an unweighted drive is the bundle's own driver clip, and starting the
 *  rest at weight 1 would average every clip into one pose. */
export class PartRigDrive {
  /**
   * @param {{name: string, scene: THREE.Object3D}[]} partScenes  one entry per part GLB;
   *   `name` is the source file's, NOT the loaded mesh's — GLTFLoader suffixes a node
   *   whose name a mesh already took (`…FaceMesh` → `…FaceMesh_1`), and the OGS branch
   *   lookup keys on the file name.
   * @param {THREE.AnimationClip|THREE.AnimationClip[]} clips  the clips every part binds
   *   by bone name; each reduced to bone rotations on the way in. The first is primary —
   *   `this.clip`, the one the unweighted drive plays.
   */
  constructor(partScenes, clips) {
    // REST FIRST, before the mixers pose anything: the hair rides this delta, measured
    // from the bind pose its trained neutral belongs to.
    this.headRest = captureHeadRestOrNull(partScenes[0]?.scene)
    // WHICH part's space that rest was measured in. The delta is conjugated into branch
    // space, and each part solves its own frame, so the caller has to hand back this
    // part's fit or the rotation happens about the wrong origin.
    this.headRestPart = partScenes[0]?.name ?? null
    this._headDelta = new THREE.Matrix4()
    // Same reason: buildRestPose reads the CURRENT pose, so capturing it late bakes a
    // POSED frame into every additive delta built against this rig.
    this.restPose = new Map()
    for (const { scene } of partScenes) {
      if (scene) for (const [name, pose] of buildRestPose(scene)) this.restPose.set(name, pose)
    }
    // Each clip states its OWN provenance; `map(poseTracksOnly)` would hand the array
    // INDEX in as the marker, so every clip after the first read as "generated, no root
    // motion" and lost its pelvis translation.
    this.clips = (Array.isArray(clips) ? clips : clips ? [clips] : [])
      .map((clip) => poseTracksOnly(clip, clip.userData?.aosGeneratedMotion ?? null))
    // A rig with no clips is normal: it is loaded for its skeleton, and what plays on it
    // arrives later through addClips.
    this.clip = this.clips[0] ?? null
    this.group = new THREE.Group()
    this.group.name = 'PartRig'
    this.meshes = {}
    // Several parts may share ONE scene — rig.glb carries every part over a single bone
    // hierarchy, so one skeleton, one mixer, and no duplicate bone names to resolve.
    const mixerFor = new Map()
    this.parts = partScenes.map(({ name, scene, mesh }) => {
      let shared = mixerFor.get(scene)
      if (!shared) {
        const mixer = new THREE.AnimationMixer(scene)
        const actions = new Map()
        for (const clip of this.clips) {
          const dropped = new Set()
          actions.set(clip.name, mixer.clipAction(clipForPart(clip, scene, scene.name || name, dropped)))
          reportDroppedBones(clip.name, dropped)
        }
        // Only the primary plays unweighted; with no clips the rig holds its bind pose.
        const action = this.clip ? actions.get(this.clip.name) : null
        action?.play()
        this.group.add(scene)
        shared = { mixer, action, actions, boundTracks: action?.getClip().tracks.length ?? 0 }
        mixerFor.set(scene, shared)
      }
      this.meshes[name] = mesh ?? partMesh(scene)
      return {
        name,
        scene,
        mixer: shared.mixer,
        action: shared.action,
        boundTracks: shared.boundTracks,
        clipTracks: this.clip?.tracks.length ?? 0,
        ...countGeometry(this.meshes[name]),
      }
    })
    this._actionSets = [...mixerFor.values()].map((entry) => entry.actions)
    // Lazily-built additive clones, one map per action set (see _additiveAction).
    this._additiveActions = this._actionSets.map(() => new Map())
    // Clips that ARRIVED as deltas — makeClipAdditive must not run on them twice.
    this._alreadyAdditive = new Set()
    // One update per distinct scene: driving a shared mixer once per part would advance
    // the clock N times a frame and play the dance N times too fast.
    this.mixers = [...mixerFor.values()].map((entry) => entry.mixer)
    this.scenes = [...mixerFor.keys()]
    // The graph's play requests that hold players on this rig (see setPlays), and the
    // setPlays call counter that tells a continuing request from a fresh one.
    this._live = []
    this._playFrame = 0
  }

  /** Drive the rig from the body blueprint's output — the subset of applyWeights that
   *  applies to a part rig. `additive` is NOT optional; a zero-weight action is stopped,
   *  and exactly one of a clip's normal/additive pair runs per frame.
   *  Notes: ~/comments/aos-threejs-poc/multipartBodyDrive_createPartRigDriver_arbitration.md */
  setWeights(weights, { additive, loop, speed } = {}) {
    for (let i = 0; i < this._actionSets.length; i++) {
      const actions = this._actionSets[i]
      for (const [name, action] of actions) {
        const weight = weights?.[name] ?? 0
        const wantAdditive = weight > 0 && additive?.[name] === true
        const clone = wantAdditive ? this._additiveAction(i, name) : this._additiveActions[i].get(name)
        // Stand the unused half of the pair down before driving the other.
        const idle = wantAdditive ? action : clone
        if (idle?.isRunning()) idle.stop()
        const driven = wantAdditive ? clone : action
        if (!driven || weight <= 0) {
          if (driven?.isRunning()) driven.stop()
          continue
        }
        const shouldLoop = loop?.[name] !== false
        const wantLoop = shouldLoop ? THREE.LoopRepeat : THREE.LoopOnce
        if (driven.loop !== wantLoop) driven.setLoop(wantLoop, shouldLoop ? Infinity : 1)
        if (driven.clampWhenFinished !== !shouldLoop) driven.clampWhenFinished = !shouldLoop
        const wantSpeed = speed?.[name] ?? 1
        if (driven.timeScale !== wantSpeed) driven.timeScale = wantSpeed
        driven.setEffectiveWeight(weight)
        if (!driven.isRunning()) { driven.reset(); driven.play() }
      }
    }
  }

  /**
   * Drive the rig from the body graph's PLAY REQUESTS — one per Play Clip node
   * (GraphEvaluator's `plays`), each on players of its own from the pool
   * (animPlayerPool.js), one per action set. The splat sibling of applyWeights: two
   * nodes on one clip keep their own time, speed and loop mode, and a node that comes
   * back (a re-entered state) starts again from frame 0.
   *
   * Each request keeps its own clip time, advanced here by `delta` × its speed — not
   * read off the absolute clock `setTime` is given, which belongs to the templates
   * (`setWeights`: a preview, the gesture). `scale` multiplies every weight: the
   * complement of a preview's crossfade, or 0 while a preview owns the rig outright,
   * which releases the players so the graph starts fresh when it takes the rig back
   * (as applyWeights does after a direct play).
   *
   * Allocation-free once a request has been seen: its rig state (`_rig`) is made once.
   */
  setPlays(plays, delta = 0, scale = 1) {
    const frame = ++this._playFrame
    for (let k = 0; k < (plays?.length ?? 0); k++) {
      const r = plays[k]
      const weight = r.weight * scale
      if (!(weight > 0)) continue
      let st = r._rig
      if (!st || st.owner !== this) {
        if (st) releaseRigPlayers(st)
        st = r._rig = { owner: this, players: [], local: 0, frame: -1, live: false }
      }
      if (st.frame === frame) continue   // the same request twice in one list
      let fresh = st.frame !== frame - 1
      const name = r.clipName
      const additive = r.additive === true
      const shouldLoop = r.loop !== false
      const wantLoop = shouldLoop ? THREE.LoopRepeat : THREE.LoopOnce
      const speed = r.speed ?? 1
      let bound = false
      for (let i = 0; i < this._actionSets.length; i++) {
        const template = additive ? this._additiveAction(i, name) : this._actionSets[i].get(name)
        if (!template) continue
        bound = true
        const source = template.getClip()
        let p = st.players[i]
        if (p && p.source !== source) { p.pool.release(p); p = null }
        if (!p) {
          p = st.players[i] = playerPoolFor(this.mixers[i]).acquire(source)
          fresh = true
        }
        const a = p.action
        const wantBlend = additive ? THREE.AdditiveAnimationBlendMode : THREE.NormalAnimationBlendMode
        if (a.blendMode !== wantBlend) a.blendMode = wantBlend
        if (a.loop !== wantLoop) a.setLoop(wantLoop, shouldLoop ? Infinity : 1)
        if (a.clampWhenFinished !== !shouldLoop) a.clampWhenFinished = !shouldLoop
        a.setEffectiveWeight(weight)
        if (!a.isRunning()) a.play()
      }
      if (!bound) continue   // a clip this rig does not carry (the driver names it)
      st.local = fresh ? 0 : st.local + delta * speed
      st.frame = frame
      if (!st.live) { st.live = true; this._live.push(r) }
    }
    // Requests not driven this call give their players back. Compacted in place.
    let keep = 0
    for (let k = 0; k < this._live.length; k++) {
      const r = this._live[k]
      if (r._rig?.owner === this && r._rig.frame === frame) this._live[keep++] = r
      else if (r._rig?.owner === this) releaseRigPlayers(r._rig)
    }
    this._live.length = keep
  }

  /** The graph players on this rig (debug / tests): `{ clipName, local, weight }`. */
  livePlays() {
    return this._live.map((r) => ({
      clipName: r.clipName, local: r._rig.local,
      weight: r._rig.players.find(Boolean)?.action.getEffectiveWeight() ?? 0,
    }))
  }

  /** The additive clone of `name` on action-set `i`, built lazily like the GLB path's
   *  ensureAdditiveClone. Clones the FILTERED per-part clip, so a part binds only its
   *  own bones. Notes: ~/comments/aos-threejs-poc/partRigDrive_addClips_double_bake.md */
  _additiveAction(i, name) {
    const built = this._additiveActions[i]
    let clone = built.get(name)
    if (clone) return clone
    const base = this._actionSets[i].get(name)
    if (!base) return null
    const additiveClip = base.getClip().clone()
    // A clip that arrived as deltas would have the gesture subtracted from itself.
    if (!this._alreadyAdditive.has(name)) THREE.AnimationUtils.makeClipAdditive(additiveClip)
    clone = this.mixers[i].clipAction(additiveClip)
    clone.blendMode = THREE.AdditiveAnimationBlendMode
    built.set(name, clone)
    return clone
  }

  /** Bind more clips to the already-built rig, by bone name. Returns the names added.
   *  The blueprint's clips arrive AFTER the rig, and rebuilding it would drop the mixers
   *  mid-drive. `replace` for a name re-serving with NEW tracks, `additiveInPlace` for a
   *  clip that already is deltas.
   *  Notes: ~/comments/aos-threejs-poc/partRigDrive_addClips_double_bake.md */
  addClips(clips, { additiveInPlace = false, replace = false, generatedMarker = null } = {}) {
    const added = []
    for (const raw of clips ?? []) {
      // The same provenance marker AvatarModel reads off the gltf scene.
      const clip = poseTracksOnly(raw, generatedMarker ?? raw.userData?.aosGeneratedMotion ?? null)
      const at = this.clips.findIndex((existing) => existing.name === clip.name)
      if (at >= 0 && !replace) continue
      if (!this._bindsAnyBone(clip)) {
        // Bound, it would pose the rig at its BIND pose — the A-pose a preview breaks as.
        console.warn(`[partRigDrive] clip '${clip.name}' binds none of this rig's bones — refused`)
        continue
      }
      if (at >= 0) this.clips[at] = clip
      else this.clips.push(clip)
      added.push(clip.name)
      if (additiveInPlace) this._alreadyAdditive.add(clip.name)
      else this._alreadyAdditive.delete(clip.name)
      const dropped = new Set()
      for (let i = 0; i < this._actionSets.length; i++) {
        const scene = this.scenes[i]
        for (const stale of [this._additiveActions[i].get(clip.name), this._actionSets[i].get(clip.name)]) {
          if (!stale) continue
          stale.stop()
          // Out of the mixer's cache too, or every regeneration leaves a stopped action behind.
          this.mixers[i].uncacheClip(stale.getClip())
          playerPoolFor(this.mixers[i]).forget(stale.getClip())
        }
        this._additiveActions[i].delete(clip.name)
        this._actionSets[i].set(clip.name,
          this.mixers[i].clipAction(clipForPart(clip, scene, scene.name || clip.name, dropped)))
      }
      reportDroppedBones(clip.name, dropped)
    }
    return added
  }

  /** Whether any of `clip`'s tracks names a node this rig actually carries. */
  _bindsAnyBone(clip) {
    return this.scenes.some((scene) => clipForPart(clip, scene, scene.name).tracks.length > 0)
  }

  /** The bound clip's length in seconds, or null when this rig cannot play it. */
  clipDuration(name) {
    return this.clips.find((clip) => clip.name === name)?.duration ?? null
  }

  /** The clip's OWN frame period, read off its keyframe times. Null when it carries too
   *  few to tell.
   *
   *  A generated clip is 30 fps (kimodo SOMA models are fixed at it), so posing between
   *  its frames spends a full lift of ~1M gaussians interpolating data that is not there.
   *  Worse for a splat than for a mixer: the root is placed at the pose that LANDED, so
   *  the extra poses do not smooth the travel, they only make the landings — and therefore
   *  the steps the root takes — uneven. */
  clipFramePeriod(name) {
    const times = this.clips.find((clip) => clip.name === name)?.tracks?.[0]?.times
    if (!times || times.length < 2) return null
    const period = (times[times.length - 1] - times[0]) / (times.length - 1)
    return period > 0 ? period : null
  }

  /** How many of this rig's bones `name` drives — zero for a clip it cannot play.
   *  A clip driving few bones leaves the rest at bind pose, which is worth reporting. */
  boundTrackCount(name) {
    const action = this._actionSets[0]?.get(name)
    return action?.getClip().tracks.length ?? 0
  }

  /** Clip names this rig can be driven with. */
  clipNames() {
    return this.clips.map((clip) => clip.name)
  }

  /** Where the head bone has moved since rest, in the BRANCH frame, at the current pose.
   *  Null when the rig carries no head bone. Reused buffer — copy anything you keep. */
  headDelta(frame = null) {
    return this.headRest ? headRigidDelta(this.headRest, this._headDelta, frame) : null
  }

  /** Playhead within the primary clip — the action's looping time, not the mixer's
   *  clock. Zero on a rig with no primary. */
  get time() {
    return this.parts[0]?.action?.time ?? 0
  }

  update(delta) {
    for (const mixer of this.mixers) mixer.update(delta)
    this.refreshBones()
  }

  /** Land the pose at `time` without advancing playback — a scrub, not a step.
   *
   *  `time` is the TEMPLATES' clock (a preview, the gesture — setWeights): each is
   *  posed at it, wrapped or clamped by its own loop mode, which is what
   *  `mixer.setTime` did. The graph's players (setPlays) are posed at their OWN clip
   *  times instead, so every action's time is written here and the mixers evaluated
   *  once with no advance — `mixer.setTime` would put every action, the graph's
   *  included, at the one absolute time. */
  setTime(time) {
    for (let i = 0; i < this._actionSets.length; i++) {
      for (const action of this._actionSets[i].values()) action.time = clipTimeAt(action, time * action.timeScale)
      for (const action of this._additiveActions[i].values()) action.time = clipTimeAt(action, time * action.timeScale)
    }
    for (let k = 0; k < this._live.length; k++) {
      const st = this._live[k]._rig
      for (const p of st.players) if (p) p.action.time = clipTimeAt(p.action, st.local)
    }
    for (const mixer of this.mixers) mixer.update(0)
    // The clip time the BONES are currently at. Read by the pose stamp
    // (`applyPartRigPose`'s `at`), so "which branch is at which time" is a
    // measurement rather than something to be inferred from the call graph.
    this.currentTime = time
    this.refreshBones()
  }

  /** Push the mixer's new bone LOCALS out to their world matrices.
   *
   *  Not optional and not a render concern: the mixer writes bone position/quaternion,
   *  while `applyBoneTransform` reads `bone.matrixWorld`. Skip this and every vertex comes
   *  back at its bind position forever — the clip's clock advances and the body does not
   *  move. A rig that happens to be mounted in a rendered scene gets this for free from
   *  three's own traversal, which is exactly why relying on that hides the bug until the
   *  first caller drives an unmounted rig. */
  refreshBones() {
    for (const scene of this.scenes) scene.updateMatrixWorld(true)
  }

  dispose() {
    for (const r of this._live) if (r._rig?.owner === this) releaseRigPlayers(r._rig)
    this._live.length = 0
    for (const mixer of this.mixers) mixer.stopAllAction()
    for (const scene of this.scenes) {
      for (const mixer of this.mixers) mixer.uncacheRoot(scene)
    }
  }
}

/** A clip time wrapped (looping) or clamped (one-shot) to `action`'s clip. */
function clipTimeAt(action, t) {
  const duration = action.getClip().duration
  if (!(duration > 0)) return 0
  if (action.loop === THREE.LoopOnce) return t < 0 ? 0 : (t > duration ? duration : t)
  const m = t % duration
  return m < 0 ? m + duration : m
}

/** Return a request's players on a rig to their pools. */
function releaseRigPlayers(st) {
  for (const p of st.players) if (p) p.pool.release(p)
  st.players.length = 0
  st.live = false
  st.frame = -1
}

/** Load ONE multi-part rig GLB, plus any clip GLBs, into a drivable rig.
 *
 *  This is the bundle's own layout: `rig.glb` holds one skinned mesh per body part over a
 *  SINGLE bone hierarchy, so a clip binds by bone name with nothing ambiguous to resolve.
 *  `partNames` is what the branch map expects to find, and a name the GLB does not carry
 *  throws rather than driving a body with a piece missing. `clipUrls` may be EMPTY — the
 *  rig is loaded for its skeleton and clips are bound to it later. */
export async function loadPartRigFromGlb(rigUrl, clipUrls = [], partNames = MYRA_PART_NAMES) {
  // Each entry is `url` or `{name, url}` — a blueprint names its clips, and a GLB's own
  // internal clip name is not reliably that name, so the caller's name wins when given.
  const wanted = (Array.isArray(clipUrls) ? clipUrls : clipUrls ? [clipUrls] : [])
    .map((entry) => (typeof entry === 'string' ? { name: null, url: entry } : entry))
    .filter((entry) => entry?.url)
  const [rig, ...clipGltfs] = await Promise.all([
    loadGltf(rigUrl), ...wanted.map((entry) => loadGltf(entry.url)),
  ])
  const clips = clipGltfs.map((gltf, i) => {
    const clip = gltf.animations[0]
    if (!clip) throw new Error(`${wanted[i].url} carries no animation clip`)
    if (wanted[i].name) clip.name = wanted[i].name
    return clip
  })
  const parts = partNames.map((name) => ({
    name, scene: rig.scene, mesh: namedPartMesh(rig.scene, name),
  }))
  return new PartRigDrive(parts, clips)
}

